import { useCallback, useState } from 'react';
import { DocumentService } from '../service/DocumentService';
import { UploadService } from '../service/UploadService';
import { categorizeFiles } from '../util/fileTypeUtils';

interface UseExternalFileDropProps {
  reactFlowInstance: any;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
  onAddNode: (node: any) => void;
  setNotification: (notification: { type: string; message: string }) => void;
  spaceId?: string;
  flowId?: string;
}

export const useExternalFileDrop = ({
  reactFlowInstance,
  reactFlowWrapper,
  onAddNode,
  setNotification,
  spaceId,
  flowId,
}: UseExternalFileDropProps) => {
  const [externalDragState, setExternalDragState] = useState<{
    isDragging: boolean;
    position: { x: number; y: number } | null;
    fileCount: number;
  }>({
    isDragging: false,
    position: null,
    fileCount: 0,
  });

  const [fileModalState, setFileModalState] = useState<{
    open: boolean;
    files: File[];
    position: { x: number; y: number } | null;
    fileName: string;
    fileType: 'pdf' | 'text';
  }>({
    open: false,
    files: [],
    position: null,
    fileName: '',
    fileType: 'pdf',
  });

  const isSimpleTextFile = (file: File): boolean => {
    const simpleTextExtensions = [
      '.txt', '.json', '.xml', '.html', '.htm', '.md', '.markdown',
      '.csv', '.log', '.sql', '.yaml', '.yml', '.toml', '.ini', '.cfg', '.conf',
    ];
    return simpleTextExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
  };

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const extractTextFromFile = useCallback(
    async (file: File, position: { x: number; y: number }) => {
      const fileName = file.name;

      try {
        setNotification({
          type: 'info',
          message: `Extracting text from ${fileName}...`,
        });

        let extractedText = '';

        // For simple text files (json, txt, csv, etc.), read directly with FileReader
        if (isSimpleTextFile(file)) {
          extractedText = (await readFileAsText(file)).trim();
        } else {
          // For complex files (docx, xlsx, pptx, etc.), use the document extraction API
          const uploadFormData = new FormData();
          uploadFormData.append('file', file);

          const uploadRes = await UploadService.uploadFile(uploadFormData, flowId);

          if (!uploadRes?.data?.src) {
            throw new Error('Failed to upload file');
          }

          const documentRes = await DocumentService.createDocument({
            name: uploadRes.data.name || fileName,
            src: uploadRes.data.src,
            spaceId: spaceId!,
          });

          // The API returns an id field even though it's not in the UploadImage type
          const documentId = (documentRes?.data as any)?.id;
          if (!documentId) {
            throw new Error('Failed to create document');
          }

          const extractRes = await DocumentService.extractTextFromFile({
            id: documentId,
          });

          extractedText = (extractRes?.data?.message ?? extractRes?.data?.src ?? '').trim();
        }

        onAddNode({
          id: `textBox_${crypto.randomUUID()}`,
          type: 'textBox',
          label: 'Text Box',
          position,
          data: {
            text: extractedText,
          },
        });

        setNotification({
          type: 'success',
          message: `Text extracted from ${fileName}`,
        });
      } catch (error: any) {
        setNotification({
          type: 'error',
          message: error.message || 'Failed to extract text',
        });
      }
    },
    [flowId, spaceId, setNotification, onAddNode],
  );

  const handleFileExtract = useCallback(async () => {
    const { files, position } = fileModalState;
    if (!files.length || !position) return;

    setFileModalState({ open: false, files: [], position: null, fileName: '', fileType: 'pdf' });

    try {
      for (const file of files) {
        await extractTextFromFile(file, position);
      }
    } catch (error) {
      console.error('Error extracting file:', error);
    }
  }, [fileModalState, extractTextFromFile]);

  const handleFileView = useCallback(() => {
    const { files, position } = fileModalState;
    if (!files.length || !position) return;

    setFileModalState({ open: false, files: [], position: null, fileName: '', fileType: 'pdf' });

    const mediaBoxWidth = 450;
    const mediaBoxHeight = 200;
    const centeredPosition = {
      x: position.x - mediaBoxWidth / 2,
      y: position.y - mediaBoxHeight / 2,
    };

    onAddNode({
      id: `mediaBox_${crypto.randomUUID()}`,
      type: 'mediaBox',
      label: 'Media Box',
      position: centeredPosition,
      data: { externalFiles: files },
    });
  }, [fileModalState, onAddNode]);

  const handleFileModalClose = useCallback(() => {
    setFileModalState({ open: false, files: [], position: null, fileName: '', fileType: 'pdf' });
  }, []);

  const onDragOver = useCallback((event: any) => {
    event.preventDefault();
    const dt = event.dataTransfer;
    const hasFiles = dt?.types?.includes('Files') || dt?.files?.length > 0;

    if (hasFiles) {
      dt.dropEffect = 'copy';
      setExternalDragState({
        isDragging: true,
        position: { x: event.clientX, y: event.clientY },
        fileCount: dt.items?.length || dt.files?.length || 1,
      });
    } else {
      dt.dropEffect = 'move';
      setExternalDragState(prev =>
        prev.isDragging ? { isDragging: false, position: null, fileCount: 0 } : prev
      );
    }
  }, []);

  const onDragLeave = useCallback((event: any) => {
    const relatedTarget = event.relatedTarget;
    if (!relatedTarget || !event.currentTarget.contains(relatedTarget)) {
      setExternalDragState({ isDragging: false, position: null, fileCount: 0 });
    }
  }, []);

  const onDrop = useCallback(
    async (event: any): Promise<boolean> => {
      event.preventDefault();
      setExternalDragState({ isDragging: false, position: null, fileCount: 0 });

      const dt = event.dataTransfer;
      const files = dt?.files;

      if (!files || files.length === 0 || !dt?.types?.includes('Files')) {
        return false;
      }

      const allFiles = Array.from(files as FileList).filter((file: File) =>
        file && typeof file === 'object'
      );

      if (allFiles.length === 0) return false;

      const { media, textBased, pdfs, unsupported } = categorizeFiles(allFiles);

      if (unsupported.length > 0) {
        setNotification({
          type: 'error',
          message: `Unsupported file types: ${unsupported.map(f => f.name).join(', ')}`,
        });
      }

      const bounds = reactFlowWrapper?.current?.getBoundingClientRect();
      const cursorPosition = reactFlowInstance?.project({
        x: event.clientX - (bounds?.left || 0),
        y: event.clientY - (bounds?.top || 0),
      });

      if (media.length > 0) {
        const mediaBoxWidth = 450;
        const mediaBoxHeight = 200;
        const position = {
          x: cursorPosition.x - mediaBoxWidth / 2,
          y: cursorPosition.y - mediaBoxHeight / 2,
        };

        onAddNode({
          id: `mediaBox_${crypto.randomUUID()}`,
          type: 'mediaBox',
          label: 'Media Box',
          position,
          data: { externalFiles: media },
        });
      }

      if (textBased.length > 0) {
        for (const file of textBased) {
          await extractTextFromFile(file, cursorPosition);
        }
      }

      if (pdfs.length > 0) {
        setFileModalState({
          open: true,
          files: pdfs,
          position: cursorPosition,
          fileName: pdfs.length === 1 ? pdfs[0].name : `${pdfs.length} PDF files`,
          fileType: 'pdf',
        });
      }

      return (media.length > 0 || textBased.length > 0 || pdfs.length > 0);
    },
    [reactFlowInstance, reactFlowWrapper, onAddNode, setNotification, extractTextFromFile],
  );

  const onPaste = useCallback(
    async (event: ClipboardEvent): Promise<boolean> => {
      const clipboardData = event.clipboardData;
      const files = clipboardData?.files;

      if (!files || files.length === 0) {
        return false;
      }

      const allFiles = Array.from(files as FileList).filter((file: File) =>
        file && typeof file === 'object'
      );

      if (allFiles.length === 0) return false;

      const { media, textBased, pdfs, unsupported } = categorizeFiles(allFiles);

      if (unsupported.length > 0) {
        setNotification({
          type: 'error',
          message: `Unsupported file types: ${unsupported.map(f => f.name).join(', ')}`,
        });
      }

      const bounds = reactFlowWrapper?.current?.getBoundingClientRect();
      const viewport = reactFlowInstance?.getViewport();

      if (!bounds || !viewport) return false;

      const centerX = bounds.width / 2;
      const centerY = bounds.height / 2;

      const flowPosition = reactFlowInstance?.project({
        x: centerX,
        y: centerY,
      });

      if (media.length > 0) {
        const mediaBoxWidth = 450;
        const mediaBoxHeight = 200;
        const position = {
          x: flowPosition.x - mediaBoxWidth / 2,
          y: flowPosition.y - mediaBoxHeight / 2,
        };

        onAddNode({
          id: `mediaBox_${crypto.randomUUID()}`,
          type: 'mediaBox',
          label: 'Media Box',
          position,
          data: { externalFiles: media },
        });
      }

      if (textBased.length > 0) {
        for (const file of textBased) {
          await extractTextFromFile(file, flowPosition);
        }
      }

      if (pdfs.length > 0) {
        setFileModalState({
          open: true,
          files: pdfs,
          position: flowPosition,
          fileName: pdfs.length === 1 ? pdfs[0].name : `${pdfs.length} PDF files`,
          fileType: 'pdf',
        });
      }

      return (media.length > 0 || textBased.length > 0 || pdfs.length > 0);
    },
    [reactFlowInstance, reactFlowWrapper, onAddNode, setNotification, extractTextFromFile],
  );

  return {
    externalDragState,
    onDragOver,
    onDragLeave,
    onDrop,
    onPaste,
    fileModalState,
    handleFileExtract,
    handleFileView,
    handleFileModalClose,
  };
};

